import { Link } from "react-router-dom";
import { Button, Dropdown, Typography } from "antd";
import {
  ChevronDown,
  Copy,
  Pause,
  Pencil,
  Play,
  RotateCcw,
  Ban,
} from "lucide-react";
import CodeStatusTag from "../_blocks/CodeStatusTag";
import {
  formatAudience,
  formatDiscount,
  formatEventsCell,
  formatUsesCell,
  formatValidCell,
  getCodeActions,
} from "./discountCodes.helpers";
import { CODES_COPY } from "./discountCodes.copy";
import { copyCode } from "./useCodeActions";

const { Text } = Typography;

/**
 * "More" menu items for a code (list row or detail header).
 * `includePauseResume` is false on the detail page, where Pause/Resume is a
 * header button of its own.
 */
export function getCodeMenuItems({ row, onAction, includePauseResume = true }) {
  const actions = getCodeActions(row.status, row.ended_reason);
  return [
    {
      key: "copy",
      label: CODES_COPY.actionCopy,
      icon: <Copy size={13} />,
      onClick: () => copyCode(row.code),
    },
    ...(includePauseResume && actions.canPause
      ? [
          {
            key: "pause",
            label: CODES_COPY.actionPause,
            icon: <Pause size={13} />,
            onClick: () => onAction(row, "PAUSE"),
          },
        ]
      : []),
    ...(includePauseResume && actions.canResume
      ? [
          {
            key: "resume",
            label: CODES_COPY.actionResume,
            icon: <Play size={13} />,
            onClick: () => onAction(row, "RESUME"),
          },
        ]
      : []),
    ...(actions.canRunAgain
      ? [
          {
            key: "run-again",
            label: CODES_COPY.actionRunAgain,
            icon: <RotateCcw size={13} />,
            onClick: () => onAction(row, "RUN_AGAIN"),
          },
        ]
      : []),
    ...(actions.canEnd
      ? [
          {
            key: "end",
            label: CODES_COPY.actionEndNow,
            icon: <Ban size={13} />,
            danger: true,
            onClick: () => onAction(row, "END"),
          },
        ]
      : []),
  ];
}

const twoLines = ({ primary, muted }) => (
  <div>
    <div>{primary}</div>
    {muted && (
      <Text type="secondary" className="text-xs">
        {muted}
      </Text>
    )}
  </div>
);

// Secondary columns are left out below md so the table fits a phone (S-237).
const PHONE_HIDDEN = ["md"];

/**
 * Columns of the codes table. The Event detail tab hides Events and Actions.
 */
export function getCodeListColumns({
  onEdit,
  onAction,
  showEvents = true,
  showActions = true,
}) {
  return [
    {
      title: "Code",
      key: "code",
      align: "center",
      render: (_, row) => (
        <Link
          to={`/shoup-community/discount-codes/${row.code_id}`}
          className="font-bold hover:underline"
        >
          {row.code}
        </Link>
      ),
    },
    {
      title: "Discount",
      key: "discount",
      align: "center",
      render: (_, row) => formatDiscount(row.discount),
    },
    {
      title: "Who can use it",
      key: "audience",
      responsive: PHONE_HIDDEN,
      align: "center",
      render: (_, row) => formatAudience(row.audience),
    },
    ...(showEvents
      ? [
          {
            title: "Events",
            key: "events",
            responsive: PHONE_HIDDEN,
            align: "center",
            render: (_, row) => twoLines(formatEventsCell(row)),
          },
        ]
      : []),
    {
      title: "Uses",
      key: "uses",
      responsive: PHONE_HIDDEN,
      align: "center",
      render: (_, row) => twoLines(formatUsesCell(row)),
    },
    {
      title: "Valid",
      key: "valid",
      responsive: PHONE_HIDDEN,
      align: "center",
      render: (_, row) => (
        <span className="text-muted-foreground text-sm">
          {formatValidCell(row)}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (_, row) => (
        <CodeStatusTag status={row.status} endedReason={row.ended_reason} />
      ),
    },
    ...(showActions
      ? [
          {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => {
              const { canEdit } = getCodeActions(row.status, row.ended_reason);
              return (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {canEdit && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<Pencil size={12} />}
                      onClick={() => onEdit(row)}
                    >
                      {CODES_COPY.actionEdit}
                    </Button>
                  )}
                  <Dropdown
                    menu={{ items: getCodeMenuItems({ row, onAction }) }}
                    trigger={["click"]}
                  >
                    <Button
                      size="small"
                      icon={<ChevronDown size={12} />}
                      iconPosition="end"
                    >
                      {CODES_COPY.actionMore}
                    </Button>
                  </Dropdown>
                </div>
              );
            },
          },
        ]
      : []),
  ];
}
